import { ShipmentTrackerStatus } from "@redotech/redo-fulfillment-group-common/outbound-shipment-label";
import { t } from "@redotech/redo-i18n";
import { DropoffStatus } from "@redotech/redo-return-core-common/dropoff/dropoff-status";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import { ReturnedProductStatus } from "@redotech/redo-return-core-common/returned-product-status";
import {
  ClaimSystemView,
  NEEDS_ATTENTION_FOLDER_NAME,
  ReturnSystemView,
  WarrantySystemView,
} from "@redotech/redo-return-operations-common/views/default-return-views";
import { ViewTypesNames } from "@redotech/redo-views-common/views";

const returnSystemViewIdentities = new Set<string>([
  ...Object.values(ReturnSystemView),
  ...Object.values(ClaimSystemView),
  ...Object.values(WarrantySystemView),
  NEEDS_ATTENTION_FOLDER_NAME,
  "complete",
  "delivered",
  "rejected",
  "reset",
]);

export function isReturnListSystemViewIdentity(value: string): boolean {
  return returnSystemViewIdentities.has(value);
}

/** Page title for the delayed-in-customs view; every table shares the view
 * name, so the title names the table's record type. */
export function getDelayedInCustomsViewTitle(table: string): string {
  switch (table) {
    case ViewTypesNames.CLAIMS:
      return t("Claims delayed in customs");
    case ViewTypesNames.WARRANTIES:
      return t("Warranties delayed in customs");
    default:
      return t("Returns delayed in customs");
  }
}

export function getReturnListSystemViewLabel(systemView: string): string {
  switch (systemView) {
    case ReturnSystemView.OPEN:
      return t("Open", {
        context:
          "currently active unfinished return status adjective like an open ticket never the verb to open",
      });
    case ReturnSystemView.IN_TRANSIT:
      return t("In transit");
    case ReturnSystemView.STUCK_IN_TRANSIT:
      return t("Stuck in transit", {
        context: "system view for returns whose transit has not progressed",
      });
    case ReturnSystemView.AWAITING_PROCESSING:
      return t("Awaiting processing");
    case ReturnSystemView.NEEDS_REVIEW:
      return t("Manual review");
    case ReturnSystemView.FLAGGED:
      return t("Flagged");
    case ReturnSystemView.FRAUD_RISK:
      return t("Fraud Risk");
    case ReturnSystemView.OUTSTANDING_BALANCE:
      return t("Outstanding balance");
    case ReturnSystemView.PARTIALLY_PROCESSED:
      return t("Partially processed");
    case ReturnSystemView.AWAITING_REMIT:
      return t("Awaiting remit", {
        context: "financial remittance after warehouse processing",
      });
    case ReturnSystemView.FAILED_RMA_CREATION:
      return t("Failed RMA creation", { context: "RMA acronym" });
    case ReturnSystemView.LABEL_CREATION_FAILED:
      return t("Label creation failed");
    case ReturnSystemView.REFUND_PAYMENT_FAILED:
      return t("Refund payment failed");
    case ReturnSystemView.RESTOCK_FAILED:
      return t("Restock failed");
    case ReturnSystemView.OUT_OF_STOCK:
      return t("Out of stock");
    case ReturnSystemView.TRACKING_EXCEPTION:
      return t("Tracking exception");
    case ReturnSystemView.DELAYED_IN_CUSTOMS:
      return t("Delayed in customs");
    case ReturnSystemView.CREATE:
      return t("Create");
    case ReturnSystemView.ALL_RETURNS:
      return t("All returns");
    case ClaimSystemView.ALL_CLAIMS:
      return t("All claims");
    case ClaimSystemView.MERCHANT_REVIEW:
      return t("Merchant review");
    case ClaimSystemView.MANAGED:
      return t("Managed");
    case WarrantySystemView.ALL_WARRANTIES:
      return t("All warranties");
    case WarrantySystemView.REPAIRS:
      return t("Repairs");
    case NEEDS_ATTENTION_FOLDER_NAME:
      return t("Needs attention");
    case "complete":
      return t("Complete", { context: "return status" });
    case "delivered":
      return t("Delivered");
    case "rejected":
      return t("Rejected");
    case "reset":
      return t("Reset", {
        context:
          "completed past state adjective return was already reset never command infinitive or gerund",
      });
    default:
      return systemView;
  }
}

export function getReturnListViewLabel(viewIdentity: string): string {
  return isReturnListSystemViewIdentity(viewIdentity)
    ? getReturnListSystemViewLabel(viewIdentity)
    : viewIdentity;
}

export function getReturnListStatusLabel(status: string): string {
  switch (status) {
    case ReturnStatus.OPEN:
      return t("Open", {
        context:
          "currently active unfinished return status adjective like an open ticket never the verb to open",
      });
    case ReturnStatus.IN_TRANSIT:
      return t("In transit");
    case ReturnStatus.DELIVERED:
      return t("Delivered");
    case ReturnStatus.NEEDS_REVIEW:
      return t("Needs review");
    case ReturnStatus.IN_REVIEW:
      return t("In review", {
        context:
          "returned item status currently inspected and graded at warehouse not book or media review",
      });
    case ReturnStatus.COMPLETE:
      return t("Complete", { context: "return status" });
    case ReturnStatus.REJECTED:
      return t("Rejected");
    case ReturnStatus.FLAGGED:
      return t("Flagged");
    case ReturnStatus.PRE_SHIPMENT:
      return t("Pre-shipment");
    case ReturnStatus.DELETED:
      return t("Reset", {
        context:
          "completed past state adjective return was already reset never command infinitive or gerund",
      });
    default:
      return status;
  }
}

export function getReturnListShipmentTrackerLabel(
  status: string | null | undefined,
): string {
  switch (status) {
    case ShipmentTrackerStatus.UNKNOWN:
    case ShipmentTrackerStatus.PRE_TRANSIT:
      return t("Pre-transit");
    case ShipmentTrackerStatus.IN_TRANSIT:
      return t("In transit");
    case ShipmentTrackerStatus.OUT_FOR_DELIVERY:
      return t("Out for delivery");
    case ShipmentTrackerStatus.AVAILABLE_FOR_PICKUP:
      return t("Available for pickup");
    case ShipmentTrackerStatus.DELIVERED:
      return t("Delivered");
    case ShipmentTrackerStatus.RETURN_TO_SENDER:
      return t("Return to sender");
    case ShipmentTrackerStatus.FAILURE:
      return t("Failure", { context: "shipment status" });
    case ShipmentTrackerStatus.CANCELLED:
      return t("Cancelled");
    case ShipmentTrackerStatus.ERROR:
      return t("Error");
    case ShipmentTrackerStatus.STALLED_IN_TRANSIT:
      return t("Stalled in transit", {
        context:
          "carrier shipment status stalled because tracking has not progressed",
      });
    case null:
    case undefined:
    case "":
      return "-";
    default:
      return status;
  }
}

export function getReturnListShipmentCsvDescription({
  daysSinceLastUpdate,
  shipmentNumber,
  status,
}: {
  daysSinceLastUpdate?: number;
  shipmentNumber: number;
  status: string | null | undefined;
}): string {
  if (daysSinceLastUpdate === undefined) {
    switch (status) {
      case ShipmentTrackerStatus.UNKNOWN:
        return t(
          "Shipment {{shipmentNumber}}: Pre-transit (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.PRE_TRANSIT:
        return t(
          "Shipment {{shipmentNumber}}: Pre-transit (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.IN_TRANSIT:
        return t(
          "Shipment {{shipmentNumber}}: In transit (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.OUT_FOR_DELIVERY:
        return t(
          "Shipment {{shipmentNumber}}: Out for delivery (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.AVAILABLE_FOR_PICKUP:
        return t(
          "Shipment {{shipmentNumber}}: Available for pickup (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.DELIVERED:
        return t("Shipment {{shipmentNumber}}: Delivered (No date available)", {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          shipmentNumber,
        });
      case ShipmentTrackerStatus.RETURN_TO_SENDER:
        return t(
          "Shipment {{shipmentNumber}}: Return to sender (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology",
            shipmentNumber,
          },
        );
      case ShipmentTrackerStatus.FAILURE:
        return t("Shipment {{shipmentNumber}}: Failure (No date available)", {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          shipmentNumber,
        });
      case ShipmentTrackerStatus.CANCELLED:
        return t("Shipment {{shipmentNumber}}: Cancelled (No date available)", {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          shipmentNumber,
        });
      case ShipmentTrackerStatus.ERROR:
        return t("Shipment {{shipmentNumber}}: Error (No date available)", {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          shipmentNumber,
        });
      case ShipmentTrackerStatus.STALLED_IN_TRANSIT:
        return t(
          "Shipment {{shipmentNumber}}: Stalled in transit (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology for carrier status stalled because tracking has not progressed",
            shipmentNumber,
          },
        );
      case null:
      case undefined:
      case "":
        return t("Shipment {{shipmentNumber}}: - (No date available)", {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          shipmentNumber,
        });
      default:
        return t(
          "Shipment {{shipmentNumber}}: {{providerStatus}} (No date available)",
          {
            context:
              "return shipment CSV sentence using consistent shipment terminology with raw carrier status",
            providerStatus: status,
            shipmentNumber,
          },
        );
    }
  }

  switch (status) {
    case ShipmentTrackerStatus.UNKNOWN:
    case ShipmentTrackerStatus.PRE_TRANSIT:
      return t(
        "Shipment {{shipmentNumber}}: Pre-transit ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          count: daysSinceLastUpdate,
          shipmentNumber,
        },
      );
    case ShipmentTrackerStatus.IN_TRANSIT:
      return t("Shipment {{shipmentNumber}}: In transit ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    case ShipmentTrackerStatus.OUT_FOR_DELIVERY:
      return t(
        "Shipment {{shipmentNumber}}: Out for delivery ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          count: daysSinceLastUpdate,
          shipmentNumber,
        },
      );
    case ShipmentTrackerStatus.AVAILABLE_FOR_PICKUP:
      return t(
        "Shipment {{shipmentNumber}}: Available for pickup ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          count: daysSinceLastUpdate,
          shipmentNumber,
        },
      );
    case ShipmentTrackerStatus.DELIVERED:
      return t("Shipment {{shipmentNumber}}: Delivered ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    case ShipmentTrackerStatus.RETURN_TO_SENDER:
      return t(
        "Shipment {{shipmentNumber}}: Return to sender ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology",
          count: daysSinceLastUpdate,
          shipmentNumber,
        },
      );
    case ShipmentTrackerStatus.FAILURE:
      return t("Shipment {{shipmentNumber}}: Failure ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    case ShipmentTrackerStatus.CANCELLED:
      return t("Shipment {{shipmentNumber}}: Cancelled ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    case ShipmentTrackerStatus.ERROR:
      return t("Shipment {{shipmentNumber}}: Error ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    case ShipmentTrackerStatus.STALLED_IN_TRANSIT:
      return t(
        "Shipment {{shipmentNumber}}: Stalled in transit ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology for carrier status stalled because tracking has not progressed",
          count: daysSinceLastUpdate,
          shipmentNumber,
        },
      );
    case null:
    case undefined:
    case "":
      return t("Shipment {{shipmentNumber}}: - ({{count}} days ago)", {
        context:
          "return shipment CSV sentence using consistent shipment terminology",
        count: daysSinceLastUpdate,
        shipmentNumber,
      });
    default:
      return t(
        "Shipment {{shipmentNumber}}: {{providerStatus}} ({{count}} days ago)",
        {
          context:
            "return shipment CSV sentence using consistent shipment terminology with raw carrier status",
          count: daysSinceLastUpdate,
          providerStatus: status,
          shipmentNumber,
        },
      );
  }
}

export function getReturnListTypeLabel(type: string): string {
  switch (type) {
    case "Exchange":
    case "exchange":
      return t("Exchange", { context: "return compensation method" });
    case "Instant Exchange":
      return t("Instant Exchange", { context: "return compensation method" });
    case "Refund":
    case "refund":
      return t("Refund", { context: "return compensation method" });
    case "Store credit":
    case "store_credit":
      return t("Store credit", {
        context:
          "customer account credit issued as return compensation not a coupon voucher gift card or currency exchange",
      });
    case "Repair":
      return t("Repair", { context: "return compensation method" });
    case "Replace":
      return t("Replace", { context: "return compensation method" });
    default:
      return type;
  }
}

export function getReturnListItemStatusLabel(status: string): string {
  switch (status) {
    case ReturnedProductStatus.OPEN:
      return t("Open", {
        context:
          "currently active unfinished return status adjective like an open ticket never the verb to open",
      });
    case ReturnedProductStatus.COMPLETE:
      return t("Complete", { context: "return status" });
    case ReturnedProductStatus.PENDING:
      return t("Pending");
    case ReturnedProductStatus.APPROVED:
      return t("Approved");
    case ReturnedProductStatus.REJECTED:
      return t("Rejected");
    case ReturnedProductStatus.PRE_SHIPMENT:
      return t("Pre-shipment");
    case ReturnedProductStatus.IN_REVIEW:
      return t("In review", {
        context:
          "returned item status currently inspected and graded at warehouse not book or media review",
      });
    default:
      return status;
  }
}

export function getReturnListDropoffStatusLabel(status: string): string {
  switch (status) {
    case DropoffStatus.AWAITING_DROPOFF:
      return t("Awaiting drop-off");
    case DropoffStatus.DROPPED_OFF:
      return t("Dropped off");
    case DropoffStatus.IN_TRANSIT:
      return t("In transit");
    case DropoffStatus.DELIVERED:
      return t("Delivered");
    default:
      return status;
  }
}
